import { Baby, RefreshCw, Check } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { Contact, formatPhoneNumber } from './FieldComponents';
import Modal from './Modal';
import { CreateContact } from './CreateContact';

export const CaseContacts = ({ lead_id }) => {
    const [contacts, setContacts] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [addContact, setAddContact] = useState(false);
    const [createContact, setCreateContact] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showRefreshCheck, setShowRefreshCheck] = useState(false);
    const [newContact, setNewContact] = useState({
        contact_id: null,
        role: '',
        lead_id: lead_id,
        notes: '',
        action: 'add_case_contact'
    });

    const fetchContacts = async () => {
        setIsRefreshing(true);
        setShowRefreshCheck(false);
        try {
            const response = await fetch(`https://api.casedb.co/contacts.php?lead_id=${lead_id}&time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setContacts(data.contacts || []);
            } else {
                console.error("Error fetching contacts:", data.message);
            }
        } catch (error) {
            console.error("Error fetching contacts:", error);
        } finally {
            setIsRefreshing(false);
            setShowRefreshCheck(true);
            setTimeout(() => setShowRefreshCheck(false), 300);
        }
    }

    const addNewContact = async () => {
        try {
            const response = await fetch('https://api.casedb.co/contacts.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(newContact)
            });
            const data = await response.json();
            if (data.success) {
                setAddContact(false);
                setNewContact({
                    contact_id: null,
                    role: 'Custom',
                    lead_id: lead_id,
                    notes: '',
                    action: 'add_case_contact'
                });
                fetchContacts();
            } else {
                console.error("Error adding contact:", data.message);
            }
        } catch (error) {
            console.error("Error adding contact:", error);
        }
    }

    const handleContactCreated = (contact) => {
        const contactId = contact?.id || contact?.contact_id || null;
        if (contactId) {
            setNewContact((prev) => ({ ...prev, contact_id: contactId }));
        }
    };

    const removeContact = async (contact_id) => {
        try {
            const response = await fetch('https://api.casedb.co/contacts.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ case_contact_id: contact_id, action: 'remove_case_contact' })
            });
            const data = await response.json();
            if (data.success) {
                fetchContacts();
            } else {
                console.error("Error removing contact:", data.message);
            }
        } catch (error) {
            console.error("Error removing contact:", error);
        }
    }

    useEffect(() => {
        fetchContacts();
    }, [lead_id]);

    const handleSort = (field) => {
        let direction = 'asc';
        if (sortConfig.key === field && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: field, direction });
    };

    const getSortValue = (contact, field) => {
        switch (field) {
            case 'name':
                return Number(contact.is_company) ? contact.company_name : contact.full_name;
            case 'role':
                return contact.field || '';
            case 'address':
                return JSON.parse(contact?.details?.find(detail => detail?.type === 'address')?.value ?? '{}')?.address || '';
            case 'email':
                return JSON.parse(contact?.details?.find(detail => detail?.type === 'email')?.value ?? '{}')?.email || '';
            case 'phone':
                return JSON.parse(contact?.details?.find(detail => detail?.type === 'phone')?.value ?? '{}')?.number || '';
            default:
                return '';
        }
    };

    const sortedContacts = useMemo(() => {
        if (!sortConfig.key) return contacts;

        return [...contacts].sort((a, b) => {
            const aValue = getSortValue(a, sortConfig.key);
            const bValue = getSortValue(b, sortConfig.key);

            let comparison = 0;
            if (aValue < bValue) {
                comparison = -1;
            } else if (aValue > bValue) {
                comparison = 1;
            }

            return sortConfig.direction === 'desc' ? comparison * -1 : comparison;
        });
    }, [contacts, sortConfig]);

    const getSortIcon = (field) => {
        if (sortConfig.key === field) {
            return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
        }
        return '';
    };

    return (
        <div className="case-section">
            <div className='case-contact-header'>
                <h2>Contacts</h2>
                <div className='case-contact-actions'>
                    <button className='action tertiary' onClick={() => setAddContact(true)}>Add Contact</button>
                    <div className='reload small-text' onClick={fetchContacts}>
                        {showRefreshCheck ? (
                            <Check size={14} />
                        ) : (
                            <RefreshCw size={14} className={isRefreshing ? 'spinner small' : undefined} />
                        )}
                        Refresh
                    </div>
                </div>
            </div>
            {contacts?.length > 0 ? (
                <table className="contacts-table default case-section-contacts">
                    <thead>
                        <tr className="default">
                            <th 
                                className="sortable" 
                                onClick={() => handleSort('name')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                <span style={{ paddingLeft: '5px' }}>Name{getSortIcon('name')}</span>
                            </th>
                            <th 
                                className="sortable" 
                                onClick={() => handleSort('role')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                Role{getSortIcon('role')}
                            </th>
                            <th 
                                className="sortable" 
                                onClick={() => handleSort('address')}
                                style={{width: '150%', cursor: 'pointer', userSelect: 'none'}}
                            >
                                Address{getSortIcon('address')}
                            </th>
                            <th 
                                className="sortable" 
                                onClick={() => handleSort('email')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                Email{getSortIcon('email')}
                            </th>
                            <th 
                                className="sortable" 
                                onClick={() => handleSort('phone')}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                Phone{getSortIcon('phone')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedContacts?.map((contact) => (
                            <tr key={contact?.id} className="contact default" onClick={() => {}}>
                                <td>
                                    {contact?.profile_picture ? (
                                        <img className="contact-initials" src={`https://api.casedb.co/${contact.profile_picture}`} alt="Profile" />
                                    ) : (
                                        <div className="contact-initials">
                                            <span>
                                                {contact?.first_name ? contact.first_name[0] : contact.company_name?.[0] || "F"}
                                                {contact?.last_name ? contact.last_name[0] : contact.company_name ? "" : "L"}
                                            </span>
                                        </div>
                                    )}
                                    {Number(contact.is_company) ? contact.company_name : `${contact?.prefix != null ? `${contact.prefix} ` : ''}${contact.full_name}${contact?.suffix != null ? ` ${contact.suffix}` : ''}`}
                                    {(Number(contact.is_company) !== 1 && new Date(contact?.date_of_birth).getTime() >= new Date(new Date().setFullYear(new Date().getFullYear() - 18))) ? <Baby size={18} /> : ""}
                                </td>
                                <td>
                                    {contact.field || contact.role || ""}
                                </td>
                                <td style={{ width: '150%' }}>
                                    {JSON.parse(contact?.details?.find(detail => detail?.type === 'address')?.value ?? '{}')?.address || ""}
                                </td>
                                <td className='subtext'>
                                    {JSON.parse(contact?.details?.find(detail => detail?.type === 'email')?.value ?? '{}')?.email || ""}
                                </td>
                                <td className='subtext'>
                                    {formatPhoneNumber(JSON.parse(contact?.details?.find(detail => detail?.type === 'phone')?.value ?? '{}')?.number) || ""}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {/*<ul>
                        {contacts.map(contact => (
                            <li key={contact.id}>
                                {contact.profile_picture && <img src={`https://api.casedb.co/${contact.profile_picture}`} alt={contact.full_name} />}
                                <span>{contact.full_name || contact.company_name}</span>
                                <span className='subtext'>{contact.field}</span>
                            </li>
                        ))}
                    </ul>*/}
                </table>
            ) : (
                <p>No contacts found for this lead.</p>
            )}
            {addContact && (
                <Modal
                    title='Add Contact'
                    onClose={() => { setAddContact(false); setNewContact({ contact_id: null, role: '', notes: '' }); }}
                    header={
                        <div className='modal-header-actions'>
                            <button className='action alt' onClick={() => { setAddContact(false); setNewContact({ contact_id: null, role: '', notes: '' }); }}>Cancel</button>
                            <button className='action' onClick={() => addNewContact()}>Save</button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper case-contact-modal'>
                        <div className='form-group full' style={{ maxWidth: '100%', margin: '0' }}>
                            <label className='subtext'>Contact</label>
                            <Contact 
                                selectedContact={newContact.contact_id}
                                setSelectedContact={(contact) => setNewContact({ ...newContact, contact_id: contact?.id })}
                                onCreateNewContact={() => setCreateContact(true)}
                            />
                        </div>
                        <div className='form-group full' style={{ maxWidth: '100%', margin: '0' }}>
                            <label className='subtext'>Contact Role</label>
                            <input
                                type='text'
                                className='default-input'
                                value={newContact.role}
                                onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                                placeholder='e.g. "Alternate Contact", "Passenger", etc.'
                            />
                        </div>
                        <div className='form-group full' style={{ maxWidth: '100%', margin: '0' }}>
                            <label className='subtext'>Notes</label>
                            <textarea
                                className='default-input'
                                value={newContact.notes}
                                onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                                placeholder='Additional details about this contact for this case (not saved to contact record)'
                                rows={4}
                            />
                        </div>
                    </div>
                </Modal>
            )}
            {createContact && (
                <CreateContact
                    setCreateContact={setCreateContact}
                    fetchContacts={fetchContacts}
                    onContactCreated={handleContactCreated}
                />
            )}
        </div>
    );
}